/**
 * The state of the machine that is rendering this screen.
 *
 * Not the account. Nothing here is a figure anybody can act on from where it
 * sits, and that is the rule: equity in a status bar is a number in the one
 * place on the page where nothing can be done about it. Account state lives in
 * the header, per §1; feed freshness lives in the strip under it; this bar
 * carries the browser's own link and the identity of the bundle it is running.
 *
 * The build identity is here because of what the 2026-08-29 production audit
 * found: a tab opened before a deployment went on running the previous bundle
 * with no way for the person looking at it to tell. Showing which commit is on
 * screen is the half of that fix the interface owns; comparing it with the
 * server's and asking for a reload is a separate change with a server side to
 * it.
 *
 * Contract §4.1.
 */
import { useEffect, useState } from "react";

import { ReleaseIndicator } from "./ReleaseIndicator";

/** Whether the browser itself believes it has a network. */
function useOnline(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    const up = (): void => {
      setOnline(true);
    };
    const down = (): void => {
      setOnline(false);
    };
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  return online;
}

export function StatusBar({ client = false }: { readonly client?: boolean }): React.JSX.Element {
  const online = useOnline();

  return (
    <footer
      data-testid="status-bar"
      className="fixed inset-x-0 bottom-0 z-30 hidden h-[30px] items-center justify-between border-t border-border-subtle bg-background-base px-4 shell:flex"
    >
      <p className="flex items-center gap-2 text-helper-sm text-text-secondary">
        <span
          aria-hidden
          className={`size-1.5 rounded-full ${
            online ? "bg-status-positive" : "bg-status-negative"
          }`}
        />
        {online ? "Соединение установлено" : "Нет соединения с сетью"}
      </p>
      {/* Release conformance is the operator's business; to a client
          "состояние релиза не подтверждено" reads as something broken. */}
      {!client && <ReleaseIndicator />}
    </footer>
  );
}
